import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ChartXY,
  ColorHEX,
  customTheme,
  FontSettings,
  LegendBox,
  LightningChart,
  lightningChart,
  LineSeries,
  SolidFill,
  SolidLine,
  Theme,
  Themes
} from '@arction/lcjs';
import { ChartsCommonService } from '../../../services/charts-common.service';
import { Destroyer } from 'app/common/utils';
import {
  debounceTime,
  skip,
  takeUntil,
} from 'rxjs/operators';
import {
  ChartTypeEnum,
  IChartFilter,
  ILCPoint,
  IPointsData,
  RealTimeEnum
} from 'app/charts-dashboard/interfaces/chart.interface';
import {
  ChartsDashboardCardQuery,
  ICard
} from 'app/charts-dashboard/state/card';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries,
  SeriesTypeEnum
} from 'app/charts-dashboard/state/series';
import {
  ChartParametersType,
  IMappedSeriesPointsResponse,
  Point,
  PointsResponseType,
  PointXType
} from 'app/charts-dashboard/interfaces/chart-http.interface';
import {
  EntityAction,
  EntityActions,
  ID
} from '@datorama/akita';
import {
  merge,
  Observable,
  of,
  Subscription,
  timer,
  zip
} from 'rxjs';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import { locale } from './locale';
import { CommonService } from 'app/core/services/common.service';
import { environment } from 'environments/environment';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-common-chart',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommonChartComponent extends Destroyer implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly chartDashboardState: ChartsDashboardStateService
  ) {
    super();

    this.lcjs = lightningChart({
      license: environment.lsjsKey,
      overrideInteractionMouseButtons: {
        chartXYPanMouseButton: 0,
        axisXYPanMouseButton: 0,
        axisXYZoomMouseButton: 2,
        chartXYRectangleZoomFitMouseButton: 2
      },
      licenseInformation: {
        company: 'Egar TECH',
        appTitle: 'IVLIVE'
      }
    });
  }

  @Input()
  public card: ICard<ID>;

  protected chart: ChartXY;
  protected theme: Theme;
  protected cursorResult: {
    x: string | Date,
    y: string,
    z: string
  };
  protected builderCursorResult;
  protected lineSeries: { [seriesId: string]: LineSeries };
  protected locale = locale;
  protected isSentUserMetrics: boolean;
  protected pointsData: IPointsData; // TODO: convert to obs?
  protected rtSubscription: Subscription;
  protected isRtActive: boolean;

  private lcjs: LightningChart;
  private legendBox: LegendBox;

  protected get mainSeries(): IChartSeries {
    if (!this.card.chartSeries.length) return;
    return this.card.chartSeries.map((id: ID) => this.seriesQuery.getEntity(id)).find((series: IChartSeries) => series?.type === SeriesTypeEnum.MAIN);
  }

  protected get allSeries(): IChartSeries[] {
    return this.card.chartSeries.map((id: ID) => this.seriesQuery.getEntity(id));
  }

  public ngOnInit(): void {
    this.createTheme();
    this.createLegendBox();
    this.seriesSubscription();
    this.loadAllSeriesPoints(true);
    this.stateSubscription();
    this.takeScreenshotSubscription();

    if (this.mainSeries.selectedFilters?.realtime?.name === RealTimeEnum.ON) {
      this.startRt();
    }
  }

  public ngAfterViewInit(): void {
    this.createChartLayout();
  }

  public ngOnDestroy() {
    this.chart.dispose();
    if (this.rtSubscription) this.rtSubscription.unsubscribe();
    super.ngOnDestroy();
  }

  private createChartLayout(): void {
    this.chart = this.lcjs.ChartXY(
      {
        container: `${this.card.id}`,
        theme: this.theme,
        height: this.elementRef.nativeElement.clientHeight
      }
    )
      .setTitle('')
      .setTitleMarginBottom(-1)
      .setTitleMarginTop(-2)
      .setPadding({left: 0, right: 8, top: 0, bottom: 0});
  }

  private createTheme(): void {
    const font = new FontSettings({
      size: 14,
      family: 'Roboto, sans-serif',
      weight: 'lighter',
      style: 'normal',
      variant: true
    });

    this.theme = customTheme(Themes.light, {
      uiFont: font,
      uiBackgroundFillStyle: new SolidFill({color: ColorHEX('#000')}),
    });
  }

  private updateCanvasContainer(): void {
    if (!this.chart.engine.container.children.length) return;

    const canvasStyle = this.chart.engine.container.children[0].getAttribute('style')
      .split('; ')
      .map((style: string) => {
        if (style.includes('min-height')) {
          return `min-height: ${this.elementRef.nativeElement.clientHeight}px`;
        }

        if (style.includes('max-height')) {
          return `max-height: ${this.elementRef.nativeElement.clientHeight}px`;
        }

        return style;
      })
      .join('; ');

    this.chart.engine.container.children[0].setAttribute('style', `${canvasStyle}`);
    this.chart.engine.layout();
    this.changeDetectorRef.detectChanges();
  }

  private stateSubscription(): void {
    merge(
      this.cardsQuery.selectEntity(this.card.id),
      this.dashboardQuery.selectDashboardLayout(),
      this.seriesQuery.selectSeriesFromCard(this.card)
    )
      .pipe(
        debounceTime(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateCanvasContainer());
  }

  private takeScreenshotSubscription(): void {
    this.chartsCommonService.$takeScreenshot
      .pipe(takeUntil(this.destroy$))
      .subscribe((cardId: string) => {
        if (cardId === this.card.id) {
          this.chart.saveToFile(
            `Screenshot-${this.chartsCommonService.getChartName(this.card.chartId).replace(' ', '-')}`,
            'image/jpeg',
            1
          );
        }
      });
  }

  private createLegendBox(): void {
    // TODO: Починить легенду
    // this.legendBox = this.chart.addLegendBox().add(this.chart);
  }

  protected seriesSubscription(): void {
    this.seriesQuery.selectEntityAction([EntityActions.Add, EntityActions.Remove])
      .pipe(takeUntil(this.destroy$))
      .subscribe((action: EntityAction<ID>) => {
        switch (action.type) {
          case EntityActions.Add:
            if (action.ids.filter(id => this.card.chartSeries.includes(id)).length) {
              this.loadSeriesPoints(action.ids, true);
            }

            if (this.mainSeries.selectedFilters.realtime?.name === RealTimeEnum.ON) {
              this.startRt();
            } else if (this.rtSubscription) {
              this.rtSubscription.unsubscribe();
              this.isRtActive = false;
            }
            break;
          case EntityActions.Remove:
            if (!this.mainSeries && this.rtSubscription) {
              this.rtSubscription.unsubscribe();
            }

            this.disposeLineChart(action.ids[0]);
            break;
        }
      });
  }

  protected startRt(): void {
    if (this.isRtActive) return;

    this.isRtActive = true;

    this.rtSubscription = timer(0, 10 * 1000)
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        zip(...this.createSeriesObservables(
          this.allSeries.filter((series: IChartSeries) => series.selectedFilters.realtime.name === RealTimeEnum.ON)
        ))
          .subscribe((res: PointsResponseType[]) => {
            res.forEach((value: PointsResponseType) => this.addPointsFromRt(value));
          });
      });
  }

  protected fillLineSeries(pointsData: PointsResponseType, isMainSeries: boolean, firstLoad?: boolean): void {
    if (this.lineSeries) {
      this.disposeLineChart(pointsData.seriesId);
    }

    if (!pointsData.points.length && firstLoad) {
      this.commonService.sendNotification(this.locale.emptyMainSeriesPointsError, 'error');
      return;
    }

    if (!this.isSentUserMetrics) {
      this.isSentUserMetrics = true;
    }
    const series: IChartSeries = this.seriesQuery.getEntity(pointsData.seriesId);
    this.chartDashboardState.removeColorFromAvailable(series.settings.color, this.card.id)
    const lineSeries: LineSeries = this.chart
      .addLineSeries()
      .setStrokeStyle(new SolidLine(
        {
          thickness: isMainSeries ? 4 : 2,
          fillStyle: new SolidFill(
            {
              color: ColorHEX(series.settings.color)
            }
          )
        })
      )
      .add(this.mapPoints(pointsData))
      .setName(this.card.chartId === ChartTypeEnum.IV_DIFFERENCE
        ? `${series.selectedFilters['stockId-first'].symbol}:${series.selectedFilters['stockId-second'].symbol}`
        : series.stock.symbol);

    this.lineSeries = {
      ...this.lineSeries,
      [pointsData.seriesId]: lineSeries
    };

    this.setCursorResultTableFormatter(this.lineSeries[pointsData.seriesId], series);
  }

  protected disposeLineChart(seriesId: ID): void {
    if (this.lineSeries[seriesId]) {
      this.lineSeries[seriesId].dispose();
      delete this.lineSeries[seriesId];
      delete this.pointsData[seriesId];
    }
  }

  protected setCursorLegendAxis(pointData: PointsResponseType): void {
    this.cursorResult = {
      x: (pointData as IMappedSeriesPointsResponse<PointXType>).nameX,
      y: (pointData as IMappedSeriesPointsResponse<PointXType>).nameY,
      z: (pointData as IMappedSeriesPointsResponse<PointXType>).nameZ
    };
  }

  // -------- Методы, которые необходим реализовать --------
  protected mapPoints(pointsData: PointsResponseType): ILCPoint[] {
    return [];
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[]): void {
  }

  protected addPointsFromRt(points: PointsResponseType): void {
  }

  protected createSeriesObservables(seriesArr: IChartSeries[], firstLoad?: boolean): Observable<PointsResponseType>[] {
    return [of()];
  }

  protected loadAllSeriesPoints(firstLoad?: boolean): void {
  }

  protected loadSeriesPoints(seriesIds: ID[], isFirstLoad?: boolean): void {
  }

  protected setTickStrategy(mainSeriesPoints: Point<PointXType>[]): void {
  }

  protected getChartsParams(series: IChartSeries, firstLoad?: boolean): ChartParametersType {
    return {} as ChartParametersType;
  }
}
